// TODO: this file is getting quite big, we should consider splitting it into multiple files, e.g. one for each
// component group (seekbar, settings, etc.)
// TODO: rename to PlayerUIFactory (createPlayerUI?) or something like that, as it creates the UI but doesn't really
// have utils functions

import {
    AudioTrackSelectBox,
    BufferingOverlay,
    CastStatusOverlay,
    Component,
    Container,
    ControlBar,
    ErrorMessageOverlay,
    FullscreenToggleButton,
    HugePlaybackToggleButton,
    Label,
    PlaybackSpeedSelectBox,
    PlaybackTimeLabelMode,
    PlaybackToggleOverlay,
    QuickSeekButton,
    SeekBar,
    SeekBarLabel,
    SettingsPanel,
    SettingsPanelItem,
    SettingsPanelPage,
    SettingsPanelPageOpenButton,
    SettingsToggleButton,
    Spacer,
    SubtitleOverlay,
    SubtitleSelectBox,
    SubtitleSettingsLabel,
    SubtitleSettingsPanelPage,
    TitleBar,
    UIContainer,
    VideoQualitySelectBox,
    VolumeSlider,
    VolumeToggleButton,
} from 'bitmovin-player-ui';
import { __ } from '../../../translations';
import h from '../../../helpers/common';
import { IUIComponents } from '../types/iPlayerUi';
import { initPlaybackTimeLabelLiveObserver, PlaybackTimeLabelLocalized } from './playbackTimeLabel';
import customSeekbarUIStructure from './customSeekbarUIStructure';

export const createPlayerUI = (
    uiComponents: IUIComponents,
    withSimpleUIInMinimizedMode?: boolean,
    isFullscreen?: boolean,
    withCustomSeekbar?: boolean
) => {
    const { renderCustomButton, renderSkipIntro, renderSkipOutro, renderWatchCredit, renderStartover, toggleMute } =
        uiComponents;

    let components = [];

    const subtitleOverlay = new SubtitleOverlay();

    const mainSettingsPanelPage = new SettingsPanelPage({
        components: [
            // TODO: update labels on language switch
            new SettingsPanelItem(__('player_lables_Automatic'), new VideoQualitySelectBox()),
            new SettingsPanelItem(__('player_lables_Audio_Track'), new AudioTrackSelectBox()),
            new SettingsPanelItem(__('player_lables_Speed'), new PlaybackSpeedSelectBox()),
        ],
    });

    const settingsPanel = new SettingsPanel({
        components: [mainSettingsPanelPage],
        hidden: true,
    });

    const subtitleSettingsPanelPage = new SubtitleSettingsPanelPage({
        settingsPanel: settingsPanel,
        overlay: subtitleOverlay,
    });

    const subtitleSelectBox = new SubtitleSelectBox();

    const subtitleSettingsOpenButton = new SettingsPanelPageOpenButton({
        targetPage: subtitleSettingsPanelPage,
        container: settingsPanel,
        ariaLabel: __('player_lables_Subtitles'),
        text: '',
    });

    mainSettingsPanelPage.addComponent(
        new SettingsPanelItem(
            new SubtitleSettingsLabel({
                text: __('player_lables_Subtitles'),
                opener: subtitleSettingsOpenButton,
            }),
            subtitleSelectBox,
            {
                role: 'menubar',
            }
        )
    );

    settingsPanel.addComponent(subtitleSettingsPanelPage);
    const volumeButtonFullPlayer = new VolumeToggleButton();
    if (toggleMute) {
        volumeButtonFullPlayer.onClick.subscribe(toggleMute);
    }

    const playbackTimeLabel = new PlaybackTimeLabelLocalized({
        timeLabelMode: PlaybackTimeLabelMode.CurrentAndTotalTime,
        cssClasses: ['ui-liveButton'],
    });

    // Keeping the playback time label's text in sync with its "live edge" state.
    initPlaybackTimeLabelLiveObserver(playbackTimeLabel);

    components = [
        ...components,
        volumeButtonFullPlayer,
        new VolumeSlider({ cssClass: 'ui-volumeslider' }),
        new Spacer(),
        new Container({
            components: [
                !withCustomSeekbar
                    ? playbackTimeLabel
                    : new Label({
                          text: `<span class="custom-playbacktime" />`,
                          cssClasses: ['custom-playbacktime-label'],
                      }),
            ],
            cssClass: 'ui-playbacktime',
        }),
        new SettingsToggleButton({ cssClass: 'ui-settingstogglebutton', settingsPanel: settingsPanel }),
    ];

    // Add custom button in control bar, e.g. playlist toggle button
    if (renderCustomButton) {
        const customButton = renderCustomButton();
        components.push(customButton);
    }

    components.push(new FullscreenToggleButton());

    // Add skip intro/outro components
    const skipIntroComponents = [];
    const { skipIntro, skipOutro } = h.getSkipVideoConfig();

    if (renderSkipIntro && skipIntro) {
        skipIntroComponents.push(renderSkipIntro());
    }
    if (renderSkipOutro && skipOutro) {
        const outroComponents: Component<any>[] = [];
        const watchCredit = renderWatchCredit && renderWatchCredit();
        const outro = renderSkipOutro && renderSkipOutro();

        if (watchCredit && watchCredit instanceof Component) {
            outroComponents.push(watchCredit);
        }
        if (outro && outro instanceof Component) {
            outroComponents.push(outro);
        }

        skipIntroComponents.push(new Container({ cssClass: 'skip-outro-buttons', components: outroComponents }));
    }

    const fullScreenUI = [
        settingsPanel,
        !withCustomSeekbar
            ? new Container({
                  components: [new SeekBar({ label: new SeekBarLabel() })],
                  cssClasses: ['controlbar-top'],
              })
            : customSeekbarUIStructure,
        new Container({
            components: components,
            cssClasses: ['controlbar-bottom'],
        }),
    ];

    const smallPlayerComponent = [];

    if (renderStartover) {
        const startOverButton = renderStartover();
        smallPlayerComponent.push(startOverButton);
        smallPlayerComponent.push(new Spacer());
    }

    const volumeButtonMiniPlayer = new VolumeToggleButton({ cssClasses: ['inside-simple-ui-layout'] });
    if (toggleMute) {
        volumeButtonMiniPlayer.onClick.subscribe(toggleMute);
    }

    /**
     * @todo - check https://cdn.bitmovin.com/player/ui/3/docs/functions/UIFactory.buildDefaultSmallScreenUI.html,
     * https://cdn.bitmovin.com/player/ui/3/docs/functions/UIFactory.modernSmallScreenUI.html
     */
    const simpleMinimizedUI = [
        settingsPanel,
        new Container({
            components: [...smallPlayerComponent, volumeButtonMiniPlayer, new FullscreenToggleButton()],
            cssClasses: ['controlbar-bottom simple-minimized-ui'],
        }),
        withSimpleUIInMinimizedMode
            ? new Container({
                  components: [new SeekBar({ label: new SeekBarLabel() })],
                  cssClasses: ['controlbar-top', 'minimized-seekbar'],
              })
            : new Spacer(),
    ];

    const controlBar = new ControlBar({
        components: isFullscreen ? fullScreenUI : withSimpleUIInMinimizedMode ? simpleMinimizedUI : fullScreenUI,
    });

    const skipButton = new Container({
        components: skipIntroComponents,
        cssClass: 'skip-container',
    });

    return new UIContainer({
        id: withCustomSeekbar ? 'custom-seekbar-ui' : 'main-ui',
        components: [
            subtitleOverlay,
            new BufferingOverlay({
                components: [
                    new Component({
                        tag: 'div',
                        cssClass: 'ui-buffering-overlay-indicator',
                        role: 'img',
                    }),
                ],
            }),
            new PlaybackToggleOverlay({
                components: [
                    // are 'fa'/'custom-info-icon' classes needed?
                    !isFullscreen && withSimpleUIInMinimizedMode
                        ? new Spacer()
                        : new QuickSeekButton({
                              cssClass: 'fa custom-info-icon ui-rewindbutton bmpui-ui-button',
                              seekSeconds: -10,
                          }),
                    new HugePlaybackToggleButton({}),
                    !isFullscreen && withSimpleUIInMinimizedMode
                        ? new Spacer()
                        : new QuickSeekButton({
                              cssClass:
                                  'fa custom-info-icon vjs-menu-button vjs-button ui-forwardbutton bmpui-ui-button',
                              seekSeconds: 10,
                          }),
                ],
            }),
            new CastStatusOverlay(),
            controlBar,
            skipButton,
            withSimpleUIInMinimizedMode ? new Spacer() : new TitleBar(),
            // new RecommendationOverlay(),
            new ErrorMessageOverlay(),
        ],
    });
};
